/** One place that decides how every state reads and which colour it wears. */
import type { SessionState } from "@contentops/contract";
import type { z } from "zod";
import type { SeriesStatus, VersionStatus } from "../api/types";

export type Tone = "ok" | "attention" | "danger" | "info" | "neutral" | "accent";
type Session = z.infer<typeof SessionState>;

export const SERIES_STATUS: Record<SeriesStatus, { label: string; tone: Tone }> = {
  planning: { label: "Planning", tone: "info" },
  awaiting_selection: { label: "Awaiting selection", tone: "attention" },
  building: { label: "Building", tone: "info" },
  in_review: { label: "In review", tone: "attention" },
  approved: { label: "Approved", tone: "ok" },
  publishing: { label: "Publishing", tone: "accent" },
  complete: { label: "Complete", tone: "ok" },
  cancelled: { label: "Cancelled", tone: "neutral" },
};

export const SESSION_STATE: Record<Session, { label: string; tone: Tone }> = {
  unopened: { label: "Not started", tone: "neutral" },
  active: { label: "Working", tone: "ok" },
  waiting: { label: "Waiting on you", tone: "attention" },
  usage_limited: { label: "Usage-limited", tone: "attention" },
  idle: { label: "Idle", tone: "neutral" },
  takeover: { label: "Taken over", tone: "info" },
  complete: { label: "Complete", tone: "ok" },
  archived: { label: "Archived", tone: "neutral" },
};

export const VERSION_STATUS: Record<VersionStatus, { label: string; tone: Tone }> = {
  building: { label: "Building", tone: "info" },
  in_review: { label: "In review", tone: "attention" },
  changes_requested: { label: "Changes requested", tone: "info" },
  approved: { label: "Approved", tone: "ok" },
  rejected: { label: "Rejected", tone: "danger" },
  stale: { label: "Stale", tone: "attention" },
  superseded: { label: "Superseded", tone: "neutral" },
};

export function publicationTone(state: string): Tone {
  if (["failed", "auth_required", "blocked"].includes(state)) return "danger";
  if (["retry_pending", "rate_limited", "paused"].includes(state)) return "attention";
  if (["published", "verified"].includes(state)) return "ok";
  if (state === "cancelled") return "neutral";
  return "info";
}

export const DECISION_LABEL: Record<string, string> = {
  plan_approval: "Plan approval",
  pronunciation_check: "Pronunciation check",
  held_content: "Held content",
  sensitive_framing: "Sensitive framing",
  missing_info: "Missing information",
  below_bar: "Below the quality bar",
  clarifying_question: "Question from the Studio",
  permission_request: "Permission request",
  source_ambiguity: "Source ambiguity",
};

/** Runner is healthy if seen in the last 60 s, stale up to 5 min, then offline. */
export function runnerHealth(lastSeen: string | null | undefined, now = Date.now()): { tone: Tone; label: string } {
  if (!lastSeen) return { tone: "danger", label: "Studio offline" };
  const age = (now - Date.parse(lastSeen)) / 1000;
  if (age < 60) return { tone: "ok", label: "Studio online" };
  if (age < 300) return { tone: "attention", label: "Studio stalled" };
  return { tone: "danger", label: "Studio offline" };
}
